import { useState } from 'react';
import {
  STAGE_MILESTONES,
  ADMIN_COUNT_DELAY_MS,
  guestFilter,
  checkinFilter,
  stageFilter,
  buildFunnelSteps,
  type FunnelStepView,
} from '@/lib/admin-aggregations';
import { adminQueryCount } from '@/client/admin.client';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

function pct(count: number, base: number): string {
  if (base === 0) return '—';
  return `${((count / base) * 100).toFixed(1)}%`;
}

function StepRow({
  label,
  count,
  total,
  prevCount,
}: {
  label: string;
  count: number;
  total: number;
  prevCount: number | null;
}) {
  const barWidth = total > 0 ? (count / total) * 100 : 0;
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between text-sm">
        <span className="text-muted-foreground">{label}</span>
        <div className="flex items-center gap-3 font-mono">
          <span className="text-xs text-muted-foreground">{pct(count, total)} of total</span>
          {prevCount !== null && (
            <span className="text-xs text-amber-500">{pct(count, prevCount)} from prev</span>
          )}
          <span className="font-semibold">{count.toLocaleString()}</span>
        </div>
      </div>
      <div className="h-2 w-full rounded-full bg-muted">
        <div
          className="h-2 rounded-full bg-primary transition-all"
          style={{ width: `${barWidth}%` }}
        />
      </div>
    </div>
  );
}

function FunnelView({ steps }: { steps: FunnelStepView[] }) {
  const total = steps.length > 0 ? steps[0].count : 0;
  return (
    <div className="flex flex-col gap-4">
      {steps.map((step, i) => (
        <StepRow
          key={step.key}
          label={step.label}
          count={step.count}
          total={total}
          prevCount={i > 0 ? steps[i - 1].count : null}
        />
      ))}
    </div>
  );
}

type GuestMode = 'include' | 'exclude';

/** 순차 카운트 — V8 는 함수명당 초당 10회만 허용하므로 한 건씩 받고 넉넉히 쉰다. */
async function fetchCountsSequential(
  queries: Parameters<typeof adminQueryCount>[0][]
): Promise<number[]> {
  const counts: number[] = [];
  for (const q of queries) {
    counts.push(await adminQueryCount(q));
    await new Promise((r) => setTimeout(r, ADMIN_COUNT_DELAY_MS));
  }
  return counts;
}

export function FunnelPanel() {
  const [steps, setSteps] = useState<FunnelStepView[] | null>(null);
  const [fetchedAt, setFetchedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<GuestMode>('include');

  const handleFetch = async (nextMode: GuestMode) => {
    setLoading(true);
    setError(null);
    try {
      const stages = STAGE_MILESTONES;
      // 기본(게스트 포함) 카운트 — 전부 단일필드/무필터라 복합 인덱스 불필요.
      const baseQueries: Parameters<typeof adminQueryCount>[0][] = [
        { collection: 'users' },
        { collection: 'users', filters: [checkinFilter()] },
        ...stages.map((n) => ({ collection: 'users', filters: [stageFilter(n)] })),
      ];
      const base = await fetchCountsSequential(baseQueries);
      let [total, checkedIn, ...stageIncl] = base;

      if (nextMode === 'exclude') {
        // 게스트 서브카운트를 구해 차감 → 비게스트만. isGuest+범위 조합이라
        // 복합 인덱스(users: isGuest+attendance.checkInCount, isGuest+lastClearedStageId)가 필요.
        const guestQueries: Parameters<typeof adminQueryCount>[0][] = [
          { collection: 'users', filters: [guestFilter()] },
          { collection: 'users', filters: [guestFilter(), checkinFilter()] },
          ...stages.map((n) => ({ collection: 'users', filters: [guestFilter(), stageFilter(n)] })),
        ];
        const [gTotal, gCheckin, ...gStage] = await fetchCountsSequential(guestQueries);
        total = Math.max(0, total - gTotal);
        checkedIn = Math.max(0, checkedIn - gCheckin);
        stageIncl = stageIncl.map((v, i) => Math.max(0, v - (gStage[i] ?? 0)));
      }

      setSteps(buildFunnelSteps({ total, checkedIn, stageIncl, stageMilestones: [...stages] }));
      setMode(nextMode);
      setFetchedAt(new Date().toLocaleTimeString());
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base">Funnel Stats</CardTitle>
            <CardDescription>
              서버 user 컬렉션 기반 진행도 집계 (
              {mode === 'exclude' ? '게스트 미포함' : '게스트 포함'}).{' '}
              {fetchedAt && <span className="font-mono text-xs">기준: {fetchedAt}</span>}
            </CardDescription>
          </div>
          <div className="flex items-center gap-1">
            <Button
              size="sm"
              variant={mode === 'include' ? 'default' : 'outline'}
              onClick={() => handleFetch('include')}
              disabled={loading}
            >
              게스트 포함
            </Button>
            <Button
              size="sm"
              variant={mode === 'exclude' ? 'default' : 'outline'}
              onClick={() => handleFetch('exclude')}
              disabled={loading}
            >
              게스트 미포함
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {steps && <FunnelView steps={steps} />}
        {!steps && !error && !loading && (
          <p className="text-sm text-muted-foreground">
            위 버튼(게스트 포함/미포함)을 눌러 데이터를 불러오세요.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
